import { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { toast } from 'sonner';
import { Camera, X, Crop, Check } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { compressImage, cropImage } from '@/utils/imageProcessing';

const API = process.env.REACT_APP_BACKEND_URL;

const uploadBlob = async (blob) => {
  const form = new FormData();
  form.append('file', blob, `evidence_${Date.now()}.jpg`);
  const r = await fetch(`${API}/api/upload/photo`, { method: 'POST', credentials: 'include', body: form });
  if (!r.ok) throw new Error('Upload échoué');
  const j = await r.json();
  return `${API}/api/files/${j.file_id}`;
};

/**
 * Uploader multi-photos avec recadrage libre et compression automatique.
 * Utilisé pour attacher des preuves visuelles à un enregistrement (infraction, encouragement).
 *
 * @param {string[]} photos  URLs des photos déjà attachées
 * @param {function(string[])} onChange  callback quand la liste change
 * @param {number} max  nombre max de photos (défaut 6)
 */
const EvidencePhotos = ({ photos = [], onChange, max = 6 }) => {
  const [cropSrc, setCropSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPixels, setAreaPixels] = useState(null);
  const [busy, setBusy] = useState(false);

  const onCropComplete = useCallback((_, pixels) => setAreaPixels(pixels), []);

  const handlePick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (photos.length >= max) {
      toast.error(`Maximum ${max} photos`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setCropSrc(reader.result);
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const confirmCrop = async () => {
    if (!cropSrc || !areaPixels) return;
    setBusy(true);
    try {
      const cropped = await cropImage(cropSrc, areaPixels);
      const compressed = await compressImage(new File([cropped], 'photo.jpg', { type: 'image/jpeg' }));
      const url = await uploadBlob(compressed);
      onChange([...photos, url]);
      toast.success('Photo attachée');
      setCropSrc(null);
      setAreaPixels(null);
      setZoom(1);
      setCrop({ x: 0, y: 0 });
    } catch (err) {
      toast.error('Erreur upload');
    }
    setBusy(false);
  };

  const skipCrop = async () => {
    if (!cropSrc) return;
    setBusy(true);
    try {
      // Convertit la data URL en Blob puis compress+upload sans recadrage
      const resp = await fetch(cropSrc);
      const blob = await resp.blob();
      const compressed = await compressImage(new File([blob], 'photo.jpg', { type: blob.type }));
      const url = await uploadBlob(compressed);
      onChange([...photos, url]);
      toast.success('Photo attachée');
      setCropSrc(null);
    } catch (err) {
      toast.error('Erreur upload');
    }
    setBusy(false);
  };

  const removePhoto = (url) => onChange(photos.filter((p) => p !== url));

  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {photos.map((url) => (
          <div key={url} className="relative group">
            <img src={url} alt="evidence" className="h-16 w-16 object-cover rounded border border-[#334155]" />
            <button
              type="button"
              onClick={() => removePhoto(url)}
              className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-[#EF4444] text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              data-testid={`remove-photo-${photos.indexOf(url)}`}
            >
              <X size={12} weight="bold" />
            </button>
          </div>
        ))}
        {photos.length < max && (
          <label className="h-16 w-16 rounded border border-dashed border-[#334155] flex flex-col items-center justify-center cursor-pointer hover:border-[#2563EB] text-[#94A3B8] text-[10px] gap-0.5" data-testid="add-evidence-photo">
            <Camera size={20} />
            Ajouter
            <input type="file" accept="image/*" capture="environment" onChange={handlePick} className="hidden" />
          </label>
        )}
      </div>
      <p className="text-[10px] text-[#64748B] mt-1">{photos.length}/{max} photo(s). Recadrage & compression automatiques.</p>

      {/* Dialog recadrage */}
      <Dialog open={!!cropSrc} onOpenChange={(o) => !o && setCropSrc(null)}>
        <DialogContent className="bg-[#0F172A] border-[#1E293B] text-[#F8FAFC] max-w-2xl">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Crop size={18} /> Recadrer la photo</DialogTitle></DialogHeader>
          <div className="relative w-full h-80 bg-black rounded-md overflow-hidden">
            {cropSrc && (
              <Cropper
                image={cropSrc}
                crop={crop}
                zoom={zoom}
                aspect={4 / 3}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            )}
          </div>
          <div className="flex items-center gap-3 pt-2">
            <label className="text-xs text-[#94A3B8] flex-shrink-0">Zoom</label>
            <input type="range" min="1" max="4" step="0.1" value={zoom} onChange={(e) => setZoom(parseFloat(e.target.value))} className="flex-1 accent-[#2563EB]" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setCropSrc(null)} className="border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B]">Annuler</Button>
            <Button variant="outline" onClick={skipCrop} disabled={busy} className="border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B]">Sans recadrage</Button>
            <Button onClick={confirmCrop} disabled={busy || !areaPixels} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="confirm-crop-btn">
              <Check size={16} className="mr-1" /> {busy ? 'Envoi...' : 'Valider'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EvidencePhotos;
